import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import useScreenOperation from './useScreenOperation';
import ScreenOperationProgress from './ScreenOperationProgress';
import '../AreaOverview/AreaOverview.css';

const EMPTY = { entries: [], total: 0, page: 1, pageSize: 50 };
const INITIAL_FILTERS = { from: '', to: '', screenId: '', action: '', status: '', origin: '', query: '' };
const ACTIONS = { start: 'Start Screen', stop: 'Stop Screen', activate_source: 'Watch source' };
Object.assign(ACTIONS, { power: 'Power', power_on: 'Power on', power_off: 'Power off', power_toggle: 'Power toggle',
  powerOn: 'Power on', powerOff: 'Power off',
  wake_on_lan: 'Wake-on-LAN', sleep_on_lan: 'Sleep-on-LAN', native_standby: 'Native standby',
  remote_action: 'Remote power', tv_remote_action: 'TV remote power', android_tv_remote_action: 'Android TV remote power',
  KEYCODE_POWER: 'Remote Power', KEYCODE_SLEEP: 'Remote Sleep', KEYCODE_WAKEUP: 'Remote Wake',
  standby: 'Standby', wake: 'Wake' });
const STATUSES = { running: 'Running', succeeded: 'Completed', succeeded_with_warnings: 'Needs checking',
  failed: 'Stopped — check devices', blocked: 'Setup needs attention', attempted: 'Attempted — outcome unknown',
  accepted: 'Command accepted — outcome unverified', verified: 'Verified', manual_required: 'Manual action required' };
const ORIGINS = { power_control: 'Power Control', scheduler: 'Screen schedule', manual_remote: 'Manual remote',
  site_control: 'Site Control', pairing_test: 'Pairing test', power_setup_test: 'Power setup test',
  device_schedule: 'Device schedule', device_remote: 'Device remote', legacy_controller: 'Controller command',
  socket_control_panel: 'Control panel command', assistant: 'Google Assistant', recovery: 'Recovery', site_routine: 'Site routine' };

function time(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

export default function PowerLogTab({ siteId, device, area, topology, onOpenPowerControl }) {
  const selectedScreen = (topology?.screens || []).find((screen) => Number(screen.televisionDeviceId) === Number(device?.id));
  const execution = useScreenOperation(area?.id, selectedScreen?.id);
  const [draft, setDraft] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(async (signal) => {
    if (!siteId) return;
    setLoading(true);
    setError('');
    try {
      const params = { page };
      Object.entries(filters).forEach(([key, value]) => { if (value) params[key] = value; });
      const response = await axios.get(`/api/site-control/sites/${siteId}/power-log`, {
        params, signal, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!signal.aborted) setData(response.data || EMPTY);
    } catch (failure) {
      if (!signal.aborted) setError(failure.response?.data?.message || 'Could not load power log.');
    } finally { if (!signal.aborted) setLoading(false); }
  }, [siteId, filters, page]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);

  const applyFilters = (event) => {
    event.preventDefault();
    setExpanded(null);
    setPage(1);
    setFilters({ ...draft });
  };
  const clearFilters = () => {
    setDraft(INITIAL_FILTERS);
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setExpanded(null);
    setRefreshKey((key) => key + 1);
  };
  const totalPages = Math.max(1, Math.ceil(data.total / 50));
  const first = data.total ? (page - 1) * 50 + 1 : 0;
  const last = Math.min(page * 50, data.total);

  return <section className="power-log" aria-label="Power log">
    <header className="power-log__header">
      <div><h2>Power Log</h2><p>Saved Site and Screen routine results, including failures and individual steps.</p></div>
      <button type="button" onClick={() => setRefreshKey((key) => key + 1)} disabled={loading || !siteId}>Refresh</button>
    </header>
    <section className="power-log__latest" aria-label="Selected Screen latest attempt">
      <p>Start/Stop runs the complete saved Screen plan, including configured sources and scheduled-content launch. Shared sources and recovery relays are protected. Only one routine runs per site.</p>
      {selectedScreen && <ScreenOperationProgress {...execution} onSetup={onOpenPowerControl} />}
    </section>
    <form className="power-log__filters" onSubmit={applyFilters}>
      <label>From <input type="date" value={draft.from} max={draft.to || undefined} onChange={(event) => setDraft({ ...draft, from: event.target.value })} /></label>
      <label>To <input type="date" value={draft.to} min={draft.from || undefined} onChange={(event) => setDraft({ ...draft, to: event.target.value })} /></label>
      <label>Screen <select value={draft.screenId} onChange={(event) => setDraft({ ...draft, screenId: event.target.value })}>
        <option value="">All Screens</option>
        {(topology?.screens || []).filter((screen) => screen.id).map((screen) => <option key={screen.id} value={screen.id}>{screen.name || `Screen ${screen.id}`}</option>)}
      </select></label>
      <label>Action <select value={draft.action} onChange={(event) => setDraft({ ...draft, action: event.target.value })}>
        <option value="">All actions</option>{Object.entries(ACTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label>
      <label>Status <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
        <option value="">All statuses</option>{Object.entries(STATUSES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label>
      <label>Origin <select value={draft.origin} onChange={(event) => setDraft({ ...draft, origin: event.target.value })}>
        <option value="">All origins</option>{Object.entries(ORIGINS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label>
      <label className="power-log__search">Search <input type="search" value={draft.query} maxLength={200} placeholder="ID, Screen, actor, reason or step" onChange={(event) => setDraft({ ...draft, query: event.target.value })} /></label>
      <button type="submit" disabled={loading}>Apply filters</button>
      <button type="button" onClick={clearFilters} disabled={loading}>Clear</button>
    </form>
    {error && <p role="alert" className="site-control-alert">{error}</p>}
    <div className="power-log__table-wrap"><table className="power-log__table">
      <thead><tr><th>Attempted</th><th>Site / Screen</th><th>Action</th><th>Status</th><th>Requested by</th><th>Details</th></tr></thead>
      <tbody>{data.entries.map((entry) => <React.Fragment key={entry.id}>
        <tr><td>{time(entry.createdAt)}</td><td>{entry.screenName || entry.targetName || `Screen ${entry.screenId || 'removed'}`}<small>{entry.areaName}</small></td>
          <td>{entry.kind === 'site_routine' ? (entry.action === 'start' ? 'Site Startup' : 'Site Shutdown') : ACTIONS[entry.action] || entry.action}</td><td>{entry.stale ? 'Interrupted or timed out' : STATUSES[entry.status] || entry.status}</td>
          <td>{['scheduler', 'device_schedule'].includes(entry.origin) ? 'Schedule'
            : entry.actorEmail || (entry.actorUserId ? `User ${entry.actorUserId}` : 'Device or system')}</td>
          <td><button type="button" aria-expanded={expanded === entry.id} onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}>{expanded === entry.id ? 'Hide' : 'View'}</button></td></tr>
        {expanded === entry.id && <tr className="power-log__detail-row"><td colSpan="6"><div className="power-log__details">
          <p><strong>Operation ID:</strong> {entry.id} · <strong>Origin:</strong> {ORIGINS[entry.origin] || entry.origin || 'Unknown'} · <strong>Completed:</strong> {time(entry.completedAt)}</p>
          {entry.method && <p><strong>Method:</strong> {entry.method}</p>}
          {entry.message && <p>{entry.message}</p>}
          {entry.result?.message && <p>{entry.result.message}</p>}
          {(entry.result?.issues || []).map((issue, index) => <p key={`issue-${index}`}>{issue.message}</p>)}
          {(entry.result?.warnings || []).map((warning, index) => <p key={`warning-${index}`}>{warning}</p>)}
          {!!entry.steps.length && <ol>{entry.steps.map((step) => <li key={step.key}><strong>{step.status}</strong> · {step.description || step.key}
            {step.evidence?.message && <small>{step.evidence.message}</small>}
            {step.evidence?.responseText != null && <small>Assistant reply: {step.evidence.responseText}</small>}
            {(step.evidence?.result?.issues || []).map((issue, index) => <small key={`issue-${index}`}>{issue.message}</small>)}
            {(step.evidence?.result?.warnings || []).map((warning, index) => <small key={`warning-${index}`}>{warning}</small>)}
            {step.evidence?.screenOperationId && <small>Screen operation: {step.evidence.screenOperationId}</small>}</li>)}</ol>}
        </div></td></tr>}
      </React.Fragment>)}</tbody>
    </table></div>
    {!loading && !error && !data.entries.length && <p className="power-log__empty">No saved power results match these filters.</p>}
    <footer className="power-log__footer"><span>{loading ? 'Loading…' : `${first}–${last} of ${data.total}`} · 50 results per page · Newest first</span>
      <div><button type="button" disabled={loading || page <= 1} onClick={() => { setPage(page - 1); setExpanded(null); }}>Previous</button>
        <span>Page {page} of {totalPages}</span>
        <button type="button" disabled={loading || page >= totalPages} onClick={() => { setPage(page + 1); setExpanded(null); }}>Next</button></div></footer>
  </section>;
}
